import { useEffect, useState } from "react";
import { useForm } from "@inertiajs/react";
import { Mail, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { NOTIFICATION_GROUPS } from "@/lib/notificationPlan";
import { Switch } from "@/components/ui/switch";

const ALL_KEYS = NOTIFICATION_GROUPS.flatMap((g) => g.emails.map((e) => e.key));

/**
 * The school's email plan: which automatic emails go to school admins,
 * teachers and families. Everything starts on. Anyone can still turn
 * emails off for themselves; sign-in links and receipts always send.
 */
export default function NotificationPlanForm({ plan, path, submitLabel = "Save email plan", returnTo = {} }) {
  const form = useForm({ enabled: plan.enabled, ...returnTo });
  const enabled = new Set(form.data.enabled);
  const toggle = (key) =>
    form.setData("enabled", enabled.has(key) ? form.data.enabled.filter((k) => k !== key) : [...form.data.enabled, key]);

  const submit = (e) => {
    e.preventDefault();
    form.patch(path, { preserveScroll: true });
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
        Anyone can still turn these off for themselves in their email preferences. Sign-in links and donation receipts always go out.
      </p>
      {NOTIFICATION_GROUPS.map((group) => (
        <fieldset key={group.audience}>
          <legend className="mb-2 flex w-full items-baseline justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {group.audience}
            <span className="font-normal normal-case tracking-normal font-tabular">
              {group.emails.filter((e) => enabled.has(e.key)).length} of {group.emails.length} on
            </span>
          </legend>
          <ul className="divide-y divide-border rounded-xl border border-border">
            {group.emails.map((email) => (
              <EmailRow
                key={email.key}
                email={email}
                audience={group.audience}
                on={enabled.has(email.key)}
                onToggle={() => toggle(email.key)}
                previewPath={plan.preview_path && `${plan.preview_path}/${email.key}`}
              />
            ))}
          </ul>
        </fieldset>
      ))}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={form.processing}>{form.processing ? "Saving…" : submitLabel}</Button>
        {form.data.enabled.length < ALL_KEYS.length && (
          <Button type="button" variant="ghost" size="sm" onClick={() => form.setData("enabled", ALL_KEYS)}>Turn them all on</Button>
        )}
      </div>
    </form>
  );
}

function EmailRow({ email, audience, on, onToggle, previewPath }) {
  const [open, setOpen] = useState(false);

  return (
    <li>
      <div className="flex items-start gap-4 px-4 py-3">
        <Mail className={cn("mt-0.5 h-4 w-4 shrink-0", on ? "text-primary" : "text-muted-foreground/50")} />
        <div className="min-w-0 flex-1">
          <p className={cn("text-sm font-medium", on ? "text-foreground" : "text-muted-foreground")}>{email.name}</p>
          <p className="text-xs leading-relaxed text-muted-foreground">{email.when}</p>
          {previewPath && (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {open ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {open ? "Hide preview" : "Preview this email"}
            </button>
          )}
        </div>
        <Switch className="mt-0.5" checked={on} onChange={onToggle} label={`${audience}: ${email.name}`} />
      </div>
      {open && <EmailPreview path={previewPath} />}
    </li>
  );
}

// Emails are laid out 560px wide; let them shrink to the frame the way a
// phone's mail app would, rather than clip.
// Appended, so the template's doctype stays first (standards mode).
const FIT_TO_FRAME = "<style>table { box-sizing: border-box; } table[width=\"560\"] { width: 100% !important; } body { margin: 0; }</style>";

// The real email template, filled with sample data, in a mail-app frame.
function EmailPreview({ path }) {
  const [preview, setPreview] = useState(null);
  const [failed, setFailed] = useState(false);
  const [height, setHeight] = useState(360);

  useEffect(() => {
    let cancelled = false;
    fetch(path, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((data) => !cancelled && setPreview(data))
      .catch(() => !cancelled && setFailed(true));
    return () => { cancelled = true; };
  }, [path]);

  return (
    <div className="border-t border-border bg-background px-4 py-4">
      {failed ? (
        <p className="text-sm text-muted-foreground">We couldn't load the preview. Try again in a moment.</p>
      ) : !preview ? (
        <p className="text-sm text-muted-foreground">Loading preview…</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
            <dl className="space-y-0.5 border-b border-border px-4 py-3 text-xs">
              <div className="flex gap-2"><dt className="w-14 text-muted-foreground">From</dt><dd className="text-foreground">PagePledge</dd></div>
              <div className="flex gap-2"><dt className="w-14 text-muted-foreground">To</dt><dd className="text-foreground">{preview.to}</dd></div>
              <div className="flex gap-2"><dt className="w-14 text-muted-foreground">Subject</dt><dd className="font-medium text-foreground">{preview.subject}</dd></div>
            </dl>
            <iframe
              title={`Preview: ${preview.subject}`}
              srcDoc={preview.html + FIT_TO_FRAME}
              sandbox="allow-same-origin"
              className="block w-full"
              style={{ height }}
              onLoad={(e) => {
                const body = e.currentTarget.contentDocument?.body;
                if (body) setHeight(Math.min(900, body.scrollHeight + 8));
              }}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Sample students, donors and numbers. Real emails use your school's own.</p>
        </>
      )}
    </div>
  );
}
